/**
 * 任务 API
 *
 * v2.0: 支持配置端点和默认参数
 */

import type { HttpClient } from '../http/HttpClient'
import type {
  TaskListResponse,
  SubmitTaskParams,
  SubmitTaskResponse,
  TaskStatusResponse,
  SquareListParams,
  SquareListResponse,
  RecommendTaskParams
} from '../../types/task'
import type { TaskAPIConfig } from '../config/TaskAPIConfig'
import {
  DEFAULT_TASK_API_CONFIG,
  validateTaskAPIConfig,
  mergeTaskAPIConfig,
  replaceEndpointParams
} from '../config/TaskAPIConfig'

export class TaskAPI {
  private http: HttpClient
  private config: TaskAPIConfig

  /**
   * 构造函数
   *
   * @param http - HTTP 客户端
   * @param config - TaskAPI 配置（可选，使用默认配置）
   */
  constructor(http: HttpClient, config?: Partial<TaskAPIConfig>) {
    this.http = http
    this.config = mergeTaskAPIConfig(config, DEFAULT_TASK_API_CONFIG)
    validateTaskAPIConfig(this.config)
  }

  /**
   * 获取任务列表
   *
   * @param styleId - 风格 ID
   * @param page - 页码
   * @param limit - 每页数量
   * @param modeTypes - 模式类型（可选，使用配置的默认值）
   */
  async getTaskList(
    styleId: number,
    page: number,
    limit: number,
    modeTypes?: string
  ): Promise<TaskListResponse> {
    const response = await this.http.get<TaskListResponse>(
      this.config.endpoints.list,
      {
        params: {
          style_id: styleId,
          page,
          limit,
          mode_types: modeTypes || this.config.defaultParams?.modeTypes
        }
      }
    )
    return response.data
  }

  /**
   * 提交生成任务
   *
   * @param params - 任务参数
   */
  async submitTask(params: SubmitTaskParams): Promise<SubmitTaskResponse> {
    const response = await this.http.post<SubmitTaskResponse>(
      this.config.endpoints.submit,
      params
    )
    return response.data
  }

  /**
   * 获取任务状态
   *
   * @param serialNo - 任务序列号
   */
  async getTaskStatus(serialNo: string): Promise<TaskStatusResponse> {
    const endpoint = replaceEndpointParams(
      this.config.endpoints.status,
      { serialNo }
    )
    const response = await this.http.get<TaskStatusResponse>(endpoint)
    return response.data
  }

  /**
   * 删除任务
   *
   * @param serialNo - 任务序列号
   */
  async deleteTask(serialNo: string): Promise<void> {
    const endpoint = replaceEndpointParams(
      this.config.endpoints.delete,
      { serialNo }
    )
    await this.http.delete(endpoint)
  }

  /**
   * 推荐到广场
   *
   * @param params - 推荐参数
   * @throws {Error} 如果未配置推荐端点
   */
  async recommendTask(params: RecommendTaskParams): Promise<void> {
    if (!this.config.endpoints.recommend) {
      throw new Error('推荐端点未配置')
    }
    await this.http.post(this.config.endpoints.recommend, params)
  }

  /**
   * 取消推荐
   *
   * @param taskId - 任务 ID
   * @throws {Error} 如果未配置取消推荐端点
   */
  async cancelRecommend(taskId: string): Promise<void> {
    if (!this.config.endpoints.cancelRecommend) {
      throw new Error('取消推荐端点未配置')
    }
    const endpoint = replaceEndpointParams(
      this.config.endpoints.cancelRecommend,
      { taskId }
    )
    await this.http.delete(endpoint)
  }

  /**
   * 获取广场列表
   *
   * @param params - 查询参数
   * @throws {Error} 如果未配置广场列表端点
   */
  async getSquareList(params: SquareListParams): Promise<SquareListResponse> {
    if (!this.config.endpoints.squareList) {
      throw new Error('广场列表端点未配置')
    }
    const response = await this.http.get<SquareListResponse>(
      this.config.endpoints.squareList,
      {
        params: {
          square_class_id: params.square_class_id,
          page: params.page,
          limit: params.limit
        }
      }
    )
    return response.data
  }
}
